import type { AnalyticsParams } from "@/lib/analytics/track"
import { trackYohoEvent } from "@/lib/analytics/track"

import type { DeviceKey, InstallMethod, EsimStatus } from "./types"

/**
 * Strongly-typed wrapper around `trackYohoEvent` for the install flow.
 *
 * Why a wrapper:
 *   - Catches typo'd event names at compile time. The install page emits 8+
 *     distinct events; a stringly-typed call site means GA dashboards
 *     silently miss data when someone fat-fingers an underscore.
 *   - Keeps the payload shape consistent. Every event gets `device` and
 *     `method` so funnel queries can group without joining tables.
 *   - Single grep target. Removing or renaming an event = change here, not
 *     spelunking through render code.
 */

type InstallEvent =
  /** User switched OS tab or Android brand pill. */
  | { name: "install_tab_changed"; device: DeviceKey; from?: DeviceKey }
  /** User switched between QR / Manual / Deeplink. */
  | {
      name: "install_method_changed"
      device: DeviceKey
      method: InstallMethod
      from?: InstallMethod
    }
  /** User copied an activation field. `field` matches what the renderer shows. */
  | {
      name: "install_copy_clicked"
      device: DeviceKey
      method: InstallMethod
      field:
        | "lpa"
        | "smdp"
        | "activation_code"
        | "matching_id"
        | "confirmation_code"
    }
  /** User downloaded the QR image to their device. */
  | {
      name: "install_qr_saved"
      device: DeviceKey
    }
  /** iOS 17.4+: user tapped the one-shot installation link. */
  | {
      name: "install_deeplink_tapped"
      device: DeviceKey
      ios_version: number | null
    }
  /** User opened the help / troubleshooting affordance. */
  | {
      name: "install_help_clicked"
      device: DeviceKey
      method: InstallMethod
      from: "fab" | "step_card" | "qr_card" | "manual_card"
    }
  /** Desktop user generated the "send guide to my phone" QR. */
  | {
      name: "install_send_to_phone"
      device: DeviceKey
      kind: "url" | "lpa"
    }
  /** Renderer flipped into the post-install success state. */
  | {
      name: "install_success_viewed"
      device: DeviceKey
      esim_status: EsimStatus
    }
  /** User picked a problem from the troubleshooting wizard. */
  | {
      name: "install_troubleshoot_picked"
      device: DeviceKey
      problem: string
    }
  /**
   * Front-end couldn't parse the LPA into SM-DP+ + Activation Code. The
   * full LPA is still shown to the user; this event just tells us how
   * often spec-noncompliant strings reach the UI so we can chase the
   * upstream source if it stays noisy. `lpa_prefix_hash` is a non-crypto
   * fingerprint of the first 24 chars — never the raw value.
   */
  | {
      name: "install_lpa_parse_failed"
      lpa_length: number
      lpa_prefix_hash: string
    }

/**
 * Emit a typed install event. Always prefer this over calling
 * `trackYohoEvent` directly from install components — the type tags catch
 * the boilerplate fields you would otherwise forget.
 *
 * Implementation note: GA / Mixpanel parameter names should match the
 * `InstallEvent` member fields verbatim. Don't munge them here.
 */
export function trackInstall(event: InstallEvent): void {
  const { name, ...payload } = event
  trackYohoEvent(name, payload as AnalyticsParams)
}
